import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { dataRoomAPI } from '../lib/api';
import {
  FolderOpen,
  File,
  Upload,
  Download,
  Trash2,
  Edit,
  Share2,
  Clock,
  Tag,
  Filter,
  Search,
  Plus,
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  MoreVertical,
  Eye,
  History,
  AlertTriangle,
  Shield,
  FileText,
  Award,
  ClipboardList,
  GraduationCap,
  Loader2,
  X,
  Check,
  Link,
  Calendar,
  FolderPlus,
  Grid,
  List,
  Copy,
  ExternalLink,
  RefreshCw
} from 'lucide-react';

const DataRoomPage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folderTree, setFolderTree] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([{ id: null, name: 'Root' }]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [expiringFiles, setExpiringFiles] = useState([]);
  
  // UI State
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [showExpired, setShowExpired] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  
  // Modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showFileDetails, setShowFileDetails] = useState(null);
  const [showShareModal, setShowShareModal] = useState(null);
  const [showVersionHistory, setShowVersionHistory] = useState(null);
  const [showAccessLog, setShowAccessLog] = useState(null);
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  
  const effectiveClientId = clientId || user?.userId || user?.id;

  useEffect(() => {
    if (effectiveClientId) {
      fetchData();
    }
  }, [effectiveClientId, currentFolder, selectedCategory, selectedTag]);

  const fetchData = async () => {
    if (!effectiveClientId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // Fetch in batches to prevent overwhelming
      const [categoriesRes, templatesRes] = await Promise.all([
        dataRoomAPI.getCategories(),
        dataRoomAPI.getTemplates()
      ]);
      
      setCategories(categoriesRes.data);
      setTemplates(templatesRes.data);
      
      const [filesRes, foldersRes, statsRes, tagsRes, expiringRes] = await Promise.all([
        dataRoomAPI.getFiles(effectiveClientId, {
          folder_id: currentFolder,
          category: selectedCategory,
          tag: selectedTag,
          search: searchQuery,
          include_expired: showExpired
        }),
        dataRoomAPI.getFolders(effectiveClientId, currentFolder),
        dataRoomAPI.getStats(effectiveClientId),
        dataRoomAPI.getTags(effectiveClientId).catch(() => ({ data: [] })),
        dataRoomAPI.getExpiringFiles(effectiveClientId, 30).catch(() => ({ data: [] }))
      ]);
      
      setFiles(filesRes.data);
      setFolders(foldersRes.data);
      setStats(statsRes.data);
      setTags(tagsRes.data || []);
      setExpiringFiles(expiringRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data room:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFolderClick = (folder) => {
    setCurrentFolder(folder.id);
    setBreadcrumbs(prev => [...prev, { id: folder.id, name: folder.name }]);
  };

  const handleBreadcrumbClick = (index) => {
    const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
    setBreadcrumbs(newBreadcrumbs);
    setCurrentFolder(newBreadcrumbs[newBreadcrumbs.length - 1].id);
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      await handleBulkUpload(Array.from(droppedFiles));
    }
  }, [currentFolder, effectiveClientId]);

  const handleBulkUpload = async (fileList) => {
    if (fileList.length === 0) return;
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      fileList.forEach(file => {
        formData.append('files', file);
      });
      formData.append('client_id', effectiveClientId);
      if (currentFolder) {
        formData.append('folder_id', currentFolder);
      }
      formData.append('category', 'general');
      
      await dataRoomAPI.bulkUpload(formData);
      await fetchData();
      setShowUploadModal(false);
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = async (file, metadata) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('client_id', effectiveClientId);
    if (currentFolder) {
      formData.append('folder_id', currentFolder);
    }
    formData.append('description', metadata.description || '');
    formData.append('tags', metadata.tags || '');
    formData.append('category', metadata.category || 'general');
    if (metadata.expirationDate) {
      formData.append('expiration_date', metadata.expirationDate);
    }
    
    setUploading(true);
    try {
      await dataRoomAPI.uploadFile(formData);
      await fetchData();
      setShowUploadModal(false);
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleCreateFolder = async (name, color) => {
    try {
      await dataRoomAPI.createFolder({
        clientId: effectiveClientId,
        name,
        parentId: currentFolder,
        color
      });
      await fetchData();
      setShowFolderModal(false);
    } catch (error) {
      console.error('Failed to create folder:', error);
    }
  };

  const handleDeleteFile = async (fileId, permanent = false) => {
    if (!window.confirm(permanent ? 'Permanently delete this file? This cannot be undone.' : 'Move this file to trash?')) {
      return;
    }
    
    try {
      await dataRoomAPI.deleteFile(fileId, permanent);
      await fetchData();
      setShowFileDetails(null);
    } catch (error) {
      console.error('Failed to delete file:', error);
    }
  };

  const handleCreateShareLink = async (fileId, options) => {
    try {
      const response = await dataRoomAPI.createShareLink(fileId, options);
      return response.data;
    } catch (error) {
      console.error('Failed to create share link:', error);
      throw error;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getCategoryIcon = (category) => {
    const icons = {
      policies: FileText,
      certificates: Award,
      contracts: File,
      reports: ClipboardList,
      evidence: Shield,
      training: GraduationCap,
      general: File
    };
    return icons[category] || File;
  };

  const getCategoryColor = (category) => {
    const colors = {
      policies: 'text-blue-400 bg-blue-500/20',
      certificates: 'text-green-400 bg-green-500/20',
      contracts: 'text-purple-400 bg-purple-500/20',
      reports: 'text-amber-400 bg-amber-500/20',
      evidence: 'text-cyan-400 bg-cyan-500/20',
      training: 'text-pink-400 bg-pink-500/20',
      general: 'text-slate-400 bg-slate-500/20'
    };
    return colors[category] || colors.general;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-violet-500 animate-spin" />
      </div>
    );
  }

  return (
    <div 
      data-testid="data-room-page" 
      className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
      onDragEnter={handleDrag}
    >
      {/* Header */}
      <header className="bg-slate-800/50 border-b border-slate-700 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <FolderOpen className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Data Room</h1>
                  <p className="text-sm text-slate-400">Secure document management</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFolderModal(true)}
                className="flex items-center gap-2 px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                data-testid="create-folder-btn"
              >
                <FolderPlus className="w-4 h-4" />
                New Folder
              </button>
              <button
                onClick={() => setShowUploadModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
                data-testid="upload-btn"
              >
                <Upload className="w-4 h-4" />
                Upload Files
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Drag & Drop Overlay */}
      {dragActive && (
        <div 
          className="fixed inset-0 bg-violet-600/20 z-50 flex items-center justify-center"
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="bg-slate-800 rounded-2xl p-12 border-2 border-dashed border-violet-500 text-center">
            <Upload className="w-16 h-16 text-violet-400 mx-auto mb-4" />
            <p className="text-xl font-semibold text-white mb-2">Drop files here</p>
            <p className="text-slate-400">Files will be uploaded to the current folder</p>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
              <div className="text-2xl font-bold text-white">{stats.totalFiles}</div>
              <div className="text-xs text-slate-400">Total Files</div>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
              <div className="text-2xl font-bold text-violet-400">{stats.totalSizeFormatted}</div>
              <div className="text-xs text-slate-400">Storage Used</div>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
              <div className="text-2xl font-bold text-blue-400">{stats.totalFolders}</div>
              <div className="text-xs text-slate-400">Folders</div>
            </div>
            <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/30">
              <div className="text-2xl font-bold text-amber-400">{stats.expiringIn30Days}</div>
              <div className="text-xs text-amber-300">Expiring Soon</div>
            </div>
            <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
              <div className="text-2xl font-bold text-red-400">{stats.expired}</div>
              <div className="text-xs text-red-300">Expired</div>
            </div>
          </div>
        )}

        {/* Expiring Files Alert */}
        {expiringFiles.length > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-400 mb-1">Documents Expiring Soon</h3>
                <p className="text-sm text-amber-200/80 mb-2">
                  {expiringFiles.length} document(s) will expire in the next 30 days
                </p>
                <div className="flex flex-wrap gap-2">
                  {expiringFiles.slice(0, 3).map((file) => (
                    <span 
                      key={file.id} 
                      className="px-2 py-1 bg-amber-500/20 text-amber-300 text-xs rounded"
                    >
                      {file.fileName} ({file.daysUntilExpiration}d)
                    </span>
                  ))}
                  {expiringFiles.length > 3 && (
                    <span className="px-2 py-1 bg-amber-500/20 text-amber-300 text-xs rounded">
                      +{expiringFiles.length - 3} more
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-1 flex-1">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.id || 'root'}>
                {index > 0 && <ChevronRight className="w-4 h-4 text-slate-500" />}
                <button
                  onClick={() => handleBreadcrumbClick(index)}
                  className={`px-2 py-1 rounded text-sm ${
                    index === breadcrumbs.length - 1
                      ? 'text-violet-400 font-medium'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {crumb.name}
                </button>
              </React.Fragment>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search files..."
              className="pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 w-48"
            />
          </div>

          {/* Category Filter */}
          <select
            value={selectedCategory || ''}
            onChange={(e) => setSelectedCategory(e.target.value || null)}
            className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>

          {/* View Toggle */}
          <div className="flex items-center gap-1 bg-slate-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-violet-600 text-white' : 'text-slate-400 hover:text-white'}`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-violet-600 text-white' : 'text-slate-400 hover:text-white'}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>

          <button
            onClick={fetchData}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className={viewMode === 'grid' ? 'grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4' : 'space-y-2'}>
          {/* Folders */}
          {folders.map((folder) => (
            <div
              key={folder.id}
              onClick={() => handleFolderClick(folder)}
              className={`cursor-pointer transition-all ${
                viewMode === 'grid'
                  ? 'bg-slate-800/50 rounded-xl p-4 border border-slate-700 hover:border-violet-500/50 hover:bg-slate-800'
                  : 'bg-slate-800/50 rounded-lg p-3 border border-slate-700 hover:border-violet-500/50 flex items-center gap-3'
              }`}
              data-testid={`folder-${folder.id}`}
            >
              <div className={`${viewMode === 'grid' ? 'w-12 h-12 mx-auto mb-3' : 'w-10 h-10'} bg-violet-500/20 rounded-lg flex items-center justify-center`}>
                <FolderOpen className="w-6 h-6 text-violet-400" />
              </div>
              <div className={viewMode === 'grid' ? 'text-center' : 'flex-1'}>
                <p className="text-white font-medium truncate">{folder.name}</p>
                {viewMode === 'list' && (
                  <p className="text-xs text-slate-400">Folder</p>
                )}
              </div>
            </div>
          ))}

          {/* Files */}
          {files.map((file) => {
            const CategoryIcon = getCategoryIcon(file.category);
            const colorClass = getCategoryColor(file.category);
            
            return (
              <div
                key={file.id}
                onClick={() => setShowFileDetails(file)}
                className={`cursor-pointer transition-all ${
                  viewMode === 'grid'
                    ? 'bg-slate-800/50 rounded-xl p-4 border border-slate-700 hover:border-violet-500/50 hover:bg-slate-800'
                    : 'bg-slate-800/50 rounded-lg p-3 border border-slate-700 hover:border-violet-500/50 flex items-center gap-3'
                } ${file.isExpired ? 'border-red-500/30' : ''}`}
                data-testid={`file-${file.id}`}
              >
                <div className={`${viewMode === 'grid' ? 'w-12 h-12 mx-auto mb-3' : 'w-10 h-10'} ${colorClass} rounded-lg flex items-center justify-center`}>
                  <CategoryIcon className="w-6 h-6" />
                </div>
                <div className={viewMode === 'grid' ? 'text-center' : 'flex-1 min-w-0'}>
                  <p className="text-white font-medium truncate">{file.fileName}</p>
                  <div className={`flex items-center gap-2 ${viewMode === 'grid' ? 'justify-center' : ''} text-xs text-slate-400`}>
                    <span>{formatFileSize(file.fileSize)}</span>
                    {file.version > 1 && (
                      <span className="px-1.5 py-0.5 bg-slate-700 rounded">v{file.version}</span>
                    )}
                    {file.isExpired && (
                      <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded">Expired</span>
                    )}
                  </div>
                </div>
                {viewMode === 'list' && (
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <span>{formatDate(file.uploadedAt)}</span>
                    <div className="flex gap-1">
                      {file.tags?.slice(0, 2).map((tag) => (
                        <span key={tag} className="px-1.5 py-0.5 bg-slate-700 rounded">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {folders.length === 0 && files.length === 0 && (
          <div className="text-center py-16">
            <FolderOpen className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No files yet</h3>
            <p className="text-slate-400 mb-6">Upload files or create folders to get started</p>
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
            >
              Upload Files
            </button>
          </div>
        )}

        {/* Document Templates */}
        <div className="mt-12">
          <h2 className="text-lg font-semibold text-white mb-4">Document Templates</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {templates.map((template) => (
              <div
                key={template.id}
                className="bg-slate-800/50 rounded-xl p-4 border border-slate-700 hover:border-violet-500/50 transition-colors"
              >
                <FileText className="w-8 h-8 text-violet-400 mb-3" />
                <h3 className="text-white font-medium mb-1">{template.name}</h3>
                <p className="text-xs text-slate-400 mb-2">{template.description}</p>
                <div className="flex flex-wrap gap-1">
                  {template.tags.slice(0, 2).map((tag) => (
                    <span key={tag} className="px-1.5 py-0.5 bg-slate-700 text-slate-300 text-xs rounded">{tag}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onUpload={handleFileUpload}
          onBulkUpload={handleBulkUpload}
          categories={categories}
          uploading={uploading}
        />
      )}

      {/* Create Folder Modal */}
      {showFolderModal && (
        <FolderModal
          onClose={() => setShowFolderModal(false)}
          onCreate={handleCreateFolder}
        />
      )}

      {/* File Details Modal */}
      {showFileDetails && (
        <FileDetailsModal
          file={showFileDetails}
          onClose={() => setShowFileDetails(null)}
          onDelete={handleDeleteFile}
          onShare={() => setShowShareModal(showFileDetails)}
          onViewVersions={() => setShowVersionHistory(showFileDetails)}
          onViewAccessLog={() => setShowAccessLog(showFileDetails)}
          formatFileSize={formatFileSize}
          formatDate={formatDate}
        />
      )}

      {/* Share Modal */}
      {showShareModal && (
        <ShareModal
          file={showShareModal}
          onClose={() => setShowShareModal(null)}
          onCreate={handleCreateShareLink}
        />
      )}
    </div>
  );
};

// Upload Modal Component
const UploadModal = ({ onClose, onUpload, onBulkUpload, categories, uploading }) => {
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [category, setCategory] = useState('general');
  const [expirationDate, setExpirationDate] = useState('');
  const [bulkFiles, setBulkFiles] = useState([]);
  const [mode, setMode] = useState('single'); // single or bulk

  const handleSubmit = () => {
    if (mode === 'single' && file) {
      onUpload(file, { description, tags: tagsInput, category, expirationDate });
    } else if (mode === 'bulk' && bulkFiles.length > 0) {
      onBulkUpload(bulkFiles);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 sticky top-0 bg-slate-800">
          <h2 className="text-lg font-semibold text-white">Upload Files</h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Mode Toggle */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setMode('single')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium ${
                mode === 'single' ? 'bg-violet-600 text-white' : 'bg-slate-700 text-slate-300'
              }`}
            >
              Single File
            </button>
            <button
              onClick={() => setMode('bulk')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium ${
                mode === 'bulk' ? 'bg-violet-600 text-white' : 'bg-slate-700 text-slate-300'
              }`}
            >
              Bulk Upload
            </button>
          </div>

          {mode === 'single' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">File</label>
                <input
                  type="file"
                  onChange={(e) => setFile(e.target.files[0])}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm resize-none"
                  placeholder="Brief description of the file..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Tags (comma-separated)</label>
                <input
                  type="text"
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                  placeholder="e.g., compliance, 2024, audit"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Expiration Date (optional)</label>
                <input
                  type="date"
                  value={expirationDate}
                  onChange={(e) => setExpirationDate(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                />
              </div>
            </>
          ) : (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Files</label>
              <input
                type="file"
                multiple
                onChange={(e) => setBulkFiles(Array.from(e.target.files))}
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
              />
              {bulkFiles.length > 0 && (
                <p className="text-sm text-slate-400 mt-2">{bulkFiles.length} file(s) selected</p>
              )}
              <p className="text-xs text-slate-500 mt-2">
                Files will be auto-categorized based on their names
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={uploading || (mode === 'single' ? !file : bulkFiles.length === 0)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Folder Modal Component
const FolderModal = ({ onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [color, setColor] = useState('violet');

  const colors = ['violet', 'blue', 'green', 'amber', 'red', 'pink'];

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-sm mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white">Create Folder</h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Folder Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
              placeholder="My Folder"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Color</label>
            <div className="flex gap-2">
              {colors.map((c) => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-lg bg-${c}-500 ${color === c ? 'ring-2 ring-white' : ''}`}
                  style={{ backgroundColor: c === 'violet' ? '#8b5cf6' : c === 'blue' ? '#3b82f6' : c === 'green' ? '#22c55e' : c === 'amber' ? '#f59e0b' : c === 'red' ? '#ef4444' : '#ec4899' }}
                />
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600"
            >
              Cancel
            </button>
            <button
              onClick={() => onCreate(name, color)}
              disabled={!name.trim()}
              className="flex-1 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50"
            >
              Create
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// File Details Modal
const FileDetailsModal = ({ file, onClose, onDelete, onShare, onViewVersions, onViewAccessLog, formatFileSize, formatDate }) => {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white truncate">{file.fileName}</h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* File Info */}
          <div className="space-y-3 mb-6">
            <div className="flex justify-between">
              <span className="text-slate-400">Size</span>
              <span className="text-white">{formatFileSize(file.fileSize)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Category</span>
              <span className="text-white capitalize">{file.category}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Version</span>
              <span className="text-white">{file.version}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Uploaded</span>
              <span className="text-white">{formatDate(file.uploadedAt)}</span>
            </div>
            {file.expirationDate && (
              <div className="flex justify-between">
                <span className="text-slate-400">Expires</span>
                <span className={file.isExpired ? 'text-red-400' : 'text-white'}>
                  {formatDate(file.expirationDate)}
                </span>
              </div>
            )}
          </div>

          {/* Tags */}
          {file.tags?.length > 0 && (
            <div className="mb-6">
              <span className="text-slate-400 text-sm">Tags</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {file.tags.map((tag) => (
                  <span key={tag} className="px-2 py-1 bg-slate-700 text-slate-300 text-sm rounded">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          {file.description && (
            <div className="mb-6">
              <span className="text-slate-400 text-sm">Description</span>
              <p className="text-white mt-1">{file.description}</p>
            </div>
          )}

          {/* Actions */}
          <div className="grid grid-cols-2 gap-3">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/dataroom/download/${file.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              Download
            </a>
            <button
              onClick={onShare}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
            <button
              onClick={onViewVersions}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              <History className="w-4 h-4" />
              Versions
            </button>
            <button
              onClick={onViewAccessLog}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              <Eye className="w-4 h-4" />
              Access Log
            </button>
          </div>

          <button
            onClick={() => onDelete(file.id, false)}
            className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Move to Trash
          </button>
        </div>
      </div>
    </div>
  );
};

// Share Modal
const ShareModal = ({ file, onClose, onCreate }) => {
  const [expiresInHours, setExpiresInHours] = useState(168);
  const [password, setPassword] = useState('');
  const [maxDownloads, setMaxDownloads] = useState('');
  const [shareLink, setShareLink] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    setLoading(true);
    try {
      const result = await onCreate(file.id, {
        expiresInHours,
        password: password || undefined,
        maxDownloads: maxDownloads ? parseInt(maxDownloads) : undefined
      });
      setShareLink(result);
    } catch (error) {
      console.error('Failed to create share link:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/shared/${shareLink.token}`;
    navigator.clipboard.writeText(url);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white">Share File</h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {shareLink ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <p className="text-green-400 font-medium mb-2">Share link created!</p>
                <p className="text-sm text-slate-300">
                  Expires: {new Date(shareLink.expiresAt).toLocaleString()}
                </p>
              </div>
              
              <div className="flex gap-2">
                <input
                  type="text"
                  value={`${window.location.origin}/shared/${shareLink.token}`}
                  readOnly
                  className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                />
                <button
                  onClick={copyLink}
                  className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              
              <button
                onClick={onClose}
                className="w-full px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
              >
                Done
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Expires In</label>
                <select
                  value={expiresInHours}
                  onChange={(e) => setExpiresInHours(parseInt(e.target.value))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                >
                  <option value={24}>24 hours</option>
                  <option value={72}>3 days</option>
                  <option value={168}>7 days</option>
                  <option value={720}>30 days</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Password (optional)</label>
                <input
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                  placeholder="Leave empty for no password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Max Downloads (optional)</label>
                <input
                  type="number"
                  value={maxDownloads}
                  onChange={(e) => setMaxDownloads(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                  placeholder="Unlimited"
                  min={1}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link className="w-4 h-4" />}
                  Create Link
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataRoomPage;
